'use strict';
/* ==========================================================================
   levels/video.js — step "video": WHO'S TELLING THIS? (DESIGN.md §5.1), the interactive video
   (shared/engine/v2/core/player.js) in the lesson's zine + case-board look (core-local/zine.js), one scene per script
   segment (content/script.json s1–s5; each segment's "visual" is the storyboard). Model: lessons/simile/levels/video.js.

   Every scene is a PURE FUNCTION of the part time t: beats are keyed to the dialogue through ctx.L / ctx.E / ctx.W
   (via cues(), which turns the ±Infinity of other parts into ±1e6), so the choreography follows whatever the voice
   timing is (the script's text timing until the clips are recorded, the clips after, with no code change) and the
   player can seek, replay and run at 25×. Branches: a reply cue is min() over the question's reply ids (branch()): in
   the reply part only the branch that plays is finite, in the lines part all are +∞ (not yet), in the after part all
   are −∞ (done).

   Honesty (DESIGN.md §4, docs/topics/facts-narrator.md): every passage on screen is Narration.SCRIPT (the model's own
   table, which tools/narrationcheck.js checks against the script and the facts): Poe's 1850 text (N13), "My Volcano",
   the three cameras and Bea's version (written for this lesson, labeled so). Every point-of-view label and every eye
   comes from Narration.pov() (its heads), every red string / evidence tag / stamp from Narration.reliability() (its
   evidence and clue ids), every clue tag's text and colour from Narration.CLUES. The only quotes that are not in the
   model: "The Eyes Have It" in Project Gutenberg #31516's wording (N15) and Booth's book and year (N7), in DICK / BOOTH
   below; Poe's first printing year 1843 (N13).

   Staging (like simile): each segment opens on a zine chapter card that tears away; Nia (left) and Ms. Park (right)
   stand full size ("two": captions in the top band on the speaker's side) or shrink to bottom-corner insets ("inset":
   captions in the bottom band beside them) when the case board needs the screen. Portrait: the player puts the
   captions in the dock; the typed passages and cards are written into the dock too (note()).
   Art: IMG.nia / nia_point / park / park_point (core-local/hooks.js poses); the new linocut props volcano, dog,
   vinegar_bottle, magnifier (DESIGN.md §2) are drawn from IMG when the assets agent's cut-outs are loaded, else by a
   code-drawn linocut stand-in baked once into a layer (prop()); Bea and the rolling eyeball are always code-drawn.
   Debug / QA: window.NarratorVideo (BODY, layers, notes: the last dock notes, marks: the reply marks drawn).
   ========================================================================== */
(function () {
  const S = Player.scenes;
  const HANDF = "'Caveat Brush'";
  const Z = () => window.Zine;
  const INK = '#141210', GREY = '#6d6a64', RED = '#d9352c', WHITE = '#fffefa', FOAM = '#f4f1e6';
  const COL = id => (window.Narration ? Narration.colour(id) : '#fff04a');
  const SCR = () => (window.Narration && Narration.SCRIPT) || {};
  const COVER_PAL = [['#141210', '#f7f5ef'], ['#f7f5ef', '#141210'], ['#fff04a', '#141210'], ['news', '#141210'], ['#ff6fbf', '#141210'], ['#141210', '#fff04a'], ['#52c8ff', '#141210']];
  // quotes and names not in the model (facts file rows cited)
  const DICK = { src: 'Philip K. Dick, "The Eyes Have It" (1953)', quote: '… his eyes slowly roved about the room.', reaction: 'The eyes had clearly come apart from the rest of him and were on their own.' };   // N15, PG #31516
  const BOOTH = { line: 'Wayne Booth, The Rhetoric of Fiction, 1961', note: 'made "unreliable narrator" a standard term' };   // N7, Correction 5
  const POE_FIRST = 1843;   // N13: first printed in The Pioneer, January 1843 (we quote the 1850 Works text)
  // the heads' colours (eye irises and the highlight of the sentence that enters that head)
  const HEAD_COL = { marcus: '#ff9d33', pepper: '#52c8ff', mom: '#72f06e', bea: '#b98aff' };
  // Florida's reasons (N1), the storyboard's wording, by kind id (Narration.KINDS)
  const REASONS = [['dishonest', 'dishonesty'], ['missing-info', 'missing information'], ['self-deceiving', 'self-deception']];

  /* ---------------- small helpers ---------------- */
  const fin = x => (x === Infinity ? 1e6 : x === -Infinity ? -1e6 : x);
  const cues = ctx => ({ L: id => fin(ctx.L(id)), E: id => fin(ctx.E(id)), W: (id, w, n = 1) => fin(ctx.W(id, w, n)) });
  const win = (t, a, b, ramp = 0.35) => Math.min(E.inOutSine(P(t, a, a + ramp)), 1 - E.inOutSine(P(t, b, b + ramp)));
  const segs = () => Game.content.script.segments;
  const segIndex = seg => segs().indexOf(seg);
  const ansOf = (ctx, part) => { const q = part.seg.question; if (!q) return null; return ctx.answer != null ? ctx.answer : part.answer != null ? part.answer : q.correct; };
  const fx = (name, alt) => (Audio.has(name) ? name : alt || null);
  function fontOk() { return typeof document === 'undefined' || !document.fonts || document.fonts.status === 'loaded'; }
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  /* the min over a question's reply ids of a cue word per branch (tbl: {replyId: word | [word, n, dt]}) */
  function branch(c, tbl) {
    let best = Infinity;
    for (const id in tbl) { const v = tbl[id]; if (v == null) continue; const [w, n, dt] = Array.isArray(v) ? v : [v, 1, 0]; best = Math.min(best, c.W(id, w, n || 1) + (dt || 0)); }
    return best;
  }
  const branchE = (c, ids) => ids.reduce((m, id) => Math.min(m, c.E(id)), Infinity);
  /* keyframed placement: keys [[t, x, y, s, rot?]] → {x, y, s, rot}; each move eases over dur from its key time */
  function track(t, keys, dur = 0.6) {
    let x = keys[0][1], y = keys[0][2], s = keys[0][3] ?? 1, r = keys[0][4] ?? 0;
    for (let i = 1; i < keys.length; i++) {
      const k = keys[i]; if (t <= k[0]) continue;
      const e = E.inOutCubic(P(t, k[0], k[0] + dur));
      x = lerp(x, k[1], e); y = lerp(y, k[2], e); s = lerp(s, k[3] ?? s, e); r = lerp(r, k[4] ?? r, e);
    }
    return { x, y, s, rot: r };
  }
  /* typing speed that finishes n characters between two cues (≥ 12 chars/s) */
  const cpsBetween = (n, a, b) => { const d = b - a; return d > 0 && d < 1e5 ? Math.max(12, n / Math.max(0.5, d * 0.9)) : 30; };
  /* the portrait dock: typed passages and cards as notes (immediate mode) */
  const NOTES = {};
  function note(key, lines) {
    NOTES[key] = lines;
    if (window.UI && UI.docked && UI.docked()) Dock.text('nv-' + key, lines, { cls: 'note' });
  }

  /* ======================================================================
     Layers: static art baked once into an off-screen canvas (the engine's OFF budget evicts them like sprites).
     ====================================================================== */
  const LY = new Map();
  function layer(key, x, y, w, h, draw, o = {}) {
    const res = +Math.min(3, Math.max(0.3, DS * (o.zoom || 1))).toFixed(3);
    let e = LY.get(key);
    if (!e || e.dead || e.res !== res) {
      if (e && !e.dead) OFF.drop(e);
      LY.delete(key);
      const pw = Math.ceil(w * res), ph = Math.ceil(h * res);
      if (!FX.sprites || typeof _bake !== 'function' || !fontOk() || !OFF.room(pw * ph * 4)) { draw(); return false; }
      const cv = _bake(pw, ph, cx => cx.setTransform(res, 0, 0, res, -x * res, -y * res), draw);
      e = OFF.add(cv, { res, x, y, w, h });
      const me = e; e.drop = () => { if (LY.get(key) === me) LY.delete(key); };
      LY.set(key, e);
    }
    e.frame = OFF.frame;
    g.drawImage(e.c, x, y, e.c.width / res, e.c.height / res);
    return true;
  }

  /* ======================================================================
     Linocut props: IMG[name] (the assets agent's cut-out) when loaded, else a code-drawn stand-in in the same style
     (black ink block, white carved lines, a white scissor-cut border), baked once. Local coords: base centre (0, 0),
     the art in x ∈ [−w/2, w/2], y ∈ [−h, 0].
     ====================================================================== */
  function border(path, lw = 18) { path(); g.lineJoin = 'round'; g.lineWidth = lw; g.strokeStyle = WHITE; g.stroke(); }
  function gouges(seed, n, x0, x1, y0, y1, alpha_ = 0.6) {
    const r = rng(seed); g.strokeStyle = `rgba(255,254,250,${alpha_})`; g.lineWidth = 2.5; g.lineCap = 'round';
    for (let i = 0; i < n; i++) { const x = x0 + r() * (x1 - x0), y = y0 + r() * (y1 - y0), a = r() * Math.PI, L = 5 + r() * 10; g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(a) * L, y + Math.sin(a) * L); g.stroke(); }
  }
  function volcanoArt() {
    const puddle = () => { g.beginPath(); g.ellipse(0, -28, 206, 26, 0, 0, TAU); };
    border(puddle, 14); g.fillStyle = FOAM; g.fill(); g.lineWidth = 4; g.strokeStyle = INK; g.stroke();
    const cone = () => { g.beginPath(); g.moveTo(-150, -34); g.lineTo(-48, -262); g.lineTo(48, -262); g.lineTo(150, -34); g.closePath(); };
    border(cone); g.fillStyle = INK; g.fill();
    // carved strata
    g.strokeStyle = WHITE; g.lineWidth = 5; g.lineCap = 'round';
    for (let k = 0; k < 5; k++) { const y = -74 - k * 40, hw = 150 - (-34 - y) * 102 / 228; g.beginPath(); g.moveTo(-hw + 16, y); g.quadraticCurveTo(0, y + 14, hw - 16, y - 4); g.stroke(); }
    gouges(611, 40, -110, 110, -240, -50);
    // the foam: out of the crater and down the right side into the puddle
    const F = [[-44, -270, 28], [-8, -292, 36], [32, -280, 30], [56, -252, 24], [70, -222, 22], [86, -190, 21], [98, -158, 20], [112, -126, 20], [124, -94, 21], [138, -62, 23], [150, -34, 22],
      [-150, -30, 16], [-114, -22, 14], [186, -30, 15], [-184, -30, 11]];
    g.fillStyle = INK; F.forEach(([x, y, r]) => { g.beginPath(); g.arc(x, y, r + 4, 0, TAU); g.fill(); });
    g.fillStyle = FOAM; F.forEach(([x, y, r]) => { g.beginPath(); g.arc(x, y, r, 0, TAU); g.fill(); });
    g.strokeStyle = 'rgba(20,18,16,0.55)'; g.lineWidth = 2.5; F.forEach(([x, y, r], i) => { if (i % 2) return; g.beginPath(); g.arc(x - r * 0.2, y - r * 0.15, r * 0.45, Math.PI * 0.9, Math.PI * 1.6); g.stroke(); });
    g.fillStyle = INK; [[-8, -290, 5], [30, -274, 4], [80, -212, 4]].forEach(([x, y, r]) => { g.beginPath(); g.arc(x, y, r, 0, TAU); g.fill(); });
  }
  function dogArt() {
    const body = () => { g.beginPath(); g.ellipse(30, -74, 150, 68, 0, 0, TAU); g.moveTo(-60, -70); g.arc(-110, -74, 56, 0, TAU); };
    border(body, 20);
    g.lineWidth = 34; g.strokeStyle = WHITE; g.lineCap = 'round'; g.beginPath(); g.moveTo(170, -90); g.quadraticCurveTo(220, -120, 196, -160); g.stroke();
    g.fillStyle = INK; body(); g.fill();
    g.lineWidth = 20; g.strokeStyle = INK; g.beginPath(); g.moveTo(170, -90); g.quadraticCurveTo(220, -120, 196, -160); g.stroke();
    // snout + ear
    g.beginPath(); g.ellipse(-168, -52, 34, 24, 0.2, 0, TAU); g.fill();
    g.strokeStyle = WHITE; g.lineWidth = 4; g.beginPath(); g.ellipse(-104, -48, 26, 44, 0.5, 0, TAU); g.stroke();
    // closed eye, nose, mouth (carved)
    g.lineWidth = 5; g.beginPath(); g.arc(-132, -84, 13, 0.2, Math.PI - 0.2); g.stroke();
    g.fillStyle = WHITE; g.beginPath(); g.arc(-196, -60, 6, 0, TAU); g.fill();
    g.beginPath(); g.moveTo(-190, -38); g.quadraticCurveTo(-170, -30, -152, -40); g.stroke();
    // tucked paws and fur
    g.beginPath(); g.moveTo(-40, -14); g.quadraticCurveTo(0, -4, 40, -14); g.stroke();
    g.beginPath(); g.moveTo(70, -14); g.quadraticCurveTo(110, -4, 140, -18); g.stroke();
    g.lineWidth = 3;
    for (let k = 0; k < 7; k++) { const x = -20 + k * 26; g.beginPath(); g.moveTo(x, -118 + (k % 2) * 6); g.quadraticCurveTo(x + 10, -100, x + 4, -86); g.stroke(); }
    gouges(612, 26, -40, 150, -120, -40, 0.45);
  }
  function bottleArt() {
    const shape = () => { g.beginPath(); g.moveTo(-50, -10); g.lineTo(-52, -190); g.quadraticCurveTo(-50, -230, -20, -248); g.lineTo(-20, -290); g.lineTo(20, -290); g.lineTo(20, -248); g.quadraticCurveTo(50, -230, 52, -190); g.lineTo(50, -10); g.closePath(); };
    border(shape); g.fillStyle = INK; g.fill();
    g.fillStyle = '#3a3632'; g.fillRect(-26, -314, 52, 28);
    g.strokeStyle = WHITE; g.lineWidth = 3; g.strokeRect(-26, -314, 52, 28);
    // the label, and a carved highlight: an empty bottle
    g.fillStyle = WHITE; g.fillRect(-42, -170, 84, 84);
    g.save(); g.translate(0, -128); g.rotate(-Math.PI / 2); g.font = "22px 'Special Elite'"; g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('VINEGAR', 0, 0); g.restore();
    g.strokeStyle = WHITE; g.lineWidth = 5; g.lineCap = 'round'; g.beginPath(); g.moveTo(-34, -60); g.lineTo(-36, -76); g.moveTo(-36, -186); g.lineTo(-34, -212); g.stroke();
  }
  function magnifierArt() {
    const cx = -34, cy = -200, R = 100;
    border(() => { g.beginPath(); g.moveTo(cx + R * 0.66, cy + R * 0.66); g.lineTo(122, -20); }, 50);
    border(() => { g.beginPath(); g.arc(cx, cy, R, 0, TAU); }, 40);
    g.lineCap = 'round'; g.strokeStyle = INK; g.lineWidth = 30; g.beginPath(); g.moveTo(cx + R * 0.72, cy + R * 0.72); g.lineTo(122, -20); g.stroke();
    g.fillStyle = 'rgba(190,225,240,0.55)'; g.beginPath(); g.arc(cx, cy, R, 0, TAU); g.fill();
    g.lineWidth = 22; g.beginPath(); g.arc(cx, cy, R, 0, TAU); g.stroke();
    g.strokeStyle = WHITE; g.lineWidth = 4; g.beginPath(); g.arc(cx, cy, R - 30, Math.PI * 1.1, Math.PI * 1.45); g.stroke();
    g.beginPath(); g.moveTo(cx + R * 0.95, cy + R * 0.95); g.lineTo(100, -40); g.stroke();
  }
  function beaArt() {
    const sil = () => {
      g.beginPath();
      g.moveTo(-84, -76); g.lineTo(-44, -222); g.lineTo(44, -222); g.lineTo(84, -76); g.closePath();
      g.moveTo(55, -276); g.arc(0, -276, 55, 0, TAU);
      g.moveTo(-40, -300); g.arc(-62, -300, 23, 0, TAU); g.moveTo(84, -300); g.arc(62, -300, 23, 0, TAU);
      g.rect(-34, -80, 22, 66); g.rect(12, -80, 22, 66);
      g.moveTo(-10, -14); g.ellipse(-26, -12, 22, 12, 0, 0, TAU); g.moveTo(44, -14); g.ellipse(28, -12, 22, 12, 0, 0, TAU);
    };
    border(sil, 18);
    g.lineCap = 'round'; g.lineWidth = 32; g.strokeStyle = WHITE; g.beginPath(); g.moveTo(40, -200); g.lineTo(96, -250); g.moveTo(-40, -200); g.lineTo(-80, -140); g.stroke();
    g.fillStyle = INK; sil(); g.fill();
    g.lineWidth = 16; g.strokeStyle = INK; g.beginPath(); g.moveTo(40, -200); g.lineTo(96, -250); g.moveTo(-40, -200); g.lineTo(-80, -140); g.stroke();
    // the crayon in her raised hand
    g.save(); g.translate(104, -262); g.rotate(-0.7); g.fillStyle = '#7b4fd0'; g.fillRect(-9, -46, 18, 56); g.beginPath(); g.moveTo(-9, -46); g.lineTo(0, -64); g.lineTo(9, -46); g.fill();
    g.strokeStyle = WHITE; g.lineWidth = 3; g.strokeRect(-9, -46, 18, 56); g.restore();
    // carved: face, pigtail ties, dress zigzag and dots
    g.fillStyle = WHITE; g.beginPath(); g.arc(-18, -284, 6, 0, TAU); g.arc(18, -284, 6, 0, TAU); g.fill();
    g.strokeStyle = WHITE; g.lineWidth = 5; g.beginPath(); g.arc(0, -266, 20, 0.25, Math.PI - 0.25); g.stroke();
    g.lineWidth = 4; g.beginPath(); g.moveTo(-44, -300); g.lineTo(-40, -290); g.moveTo(44, -300); g.lineTo(40, -290); g.stroke();
    g.beginPath(); for (let k = 0; k <= 8; k++) { const x = -72 + k * 18, y = -96 - (k % 2) * 14; if (k) g.lineTo(x, y); else g.moveTo(x, y); } g.stroke();
    [[-20, -180], [16, -160], [-6, -136], [30, -128], [-34, -140]].forEach(([x, y]) => { g.beginPath(); g.arc(x, y, 5, 0, TAU); g.fill(); });
    gouges(613, 22, -60, 60, -210, -110, 0.45);
  }
  const PROPS = {
    volcano: { w: 440, h: 330, draw: volcanoArt },
    dog: { w: 460, h: 180, draw: dogArt },
    vinegar_bottle: { w: 150, h: 330, draw: bottleArt },
    magnifier: { w: 330, h: 330, draw: magnifierArt },
    bea: { w: 250, h: 350, draw: beaArt },
  };
  /* a prop, base centre at (x, y), h tall: the cut-out when it is loaded, else the stand-in */
  function prop(name, x, y, h, o = {}) {
    const s = o.s ?? 1; if (s <= 0.001) return;
    if (IMG[name] && name !== 'bea') { Z().art(name, x, y, h, { ax: 0.5, ay: 1, rot: o.rot || 0, s, seed: o.seed || 5 }); return; }
    const A = PROPS[name]; if (!A) return;
    const k = h / A.h, zoom = Math.max(0.25, Math.round(k * 4) / 4);
    g.save(); g.translate(x, y); g.rotate(o.rot || 0); g.scale(k * s, k * s);
    layer('prop_' + name, -A.w / 2 - 12, -A.h - 12, A.w + 24, A.h + 24, A.draw, { zoom });
    g.restore();
  }

  /* ---------------- paper bits ---------------- */
  function strike(x0, y0, x1, y1, prog, seed = 1, color = RED, w = 8) { ink([[x0, y0], [(x0 + x1) / 2 + 4, (y0 + y1) / 2 - 3], [x1, y1]], { w, color, seed, prog, passes: 2, step: 10 }); }
  /* a sticky note with hand-written lines (x, y = centre) */
  function sticky(lines, x, y, t0, o = {}) {
    const s = pop(T, t0, 0.35); if (s <= 0) return;
    const w = o.w || 300, size = o.size || 34, seed = o.seed || 610, h = Math.round(40 + lines.length * size * 1.12);
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.05); g.scale(s, s);
    paperRect(0, 0, w, h, o.color || '#fff27a', { seed, depth: 1.2 });
    lines.forEach((l, i) => hand(l, 0, -h / 2 + 20 + size * 0.6 + i * size * 1.12, size, t0 + 0.1 + i * 0.15, { color: o.ink || INK, seed: seed + i + 1, dur: 0.3 }));
    tape(0, -h / 2 + 2, 90, 0.05, undefined, seed + 20);
    g.restore();
  }
  /* a typed slip (a photocopied strip sized to its text), x, y = centre */
  function slip(str, x, y, size, t0, o = {}) {
    const Zn = Z(), w_ = Zn.measure(str, size), pad = o.pad ?? size * 0.9;
    const w = Math.round(w_ + pad * 2), h = Math.round(size * 1.9);
    if (T < t0 - 0.05) return;
    const k = pop(T, t0 - 0.05, 0.3, 1.6);
    g.save(); g.translate(x, y); g.rotate(o.rot ?? 0); g.scale(k, k); g.translate(-x, -y);
    Zn.page(x, y, w, h, { seed: o.seed || 50, dark: 0.35, depth: 1.1 });
    Zn.type(str, x - w_ / 2, y, size, { t0, cps: o.cps || 40, seed: o.seed || 50, color: o.color || INK });
    g.restore();
  }
  /* a rubber stamp (no knock-out specks: they would punch through the whole frame) */
  function stampMark(text, x, y, s, o = {}) {
    if (s <= 0.001) return;
    const size = o.size || 72, color = o.color || RED, font = `${size}px 'Special Elite'`;
    const w = Math.round(tw(text, font) + size * 0.9), h = Math.round(size * 1.5);
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.12); g.scale(s, s); g.globalAlpha *= o.alpha ?? 0.9;
    g.strokeStyle = color; g.fillStyle = color; g.lineJoin = 'round';
    g.lineWidth = size * 0.1; pathPts(rectPts(w, h, 40 + text.length, true, 4)); g.stroke();
    g.lineWidth = size * 0.035; pathPts(rectPts(w - Math.round(size * 0.26), h - Math.round(size * 0.26), 41 + text.length, true, 3)); g.stroke();
    g.font = font; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(text, 0, size * 0.07);
    g.restore();
  }
  const stampS = (t, t0) => (t < t0 ? 0 : E.outBack(P(t, t0, t0 + 0.25), 1.6));
  /* a plain tag chip (neon paper strip + typed text), x, y = centre; returns its width */
  function chip(text, x, y, colour, o = {}) {
    const size = o.size || 30, w = Math.round(Z().measure(text, size) + size * 1.3), h = Math.round(size * 1.5), s = o.s ?? 1;
    if (s <= 0.001) return w;
    g.save(); g.translate(x, y); g.rotate(o.rot || 0); g.scale(s, s);
    paperRect(0, 0, w, h, colour, { seed: o.seed || 5, depth: 0.7, boil: false });
    Z().type(text, 0, size * 0.04, size, { align: 'center', seed: o.seed || 5 });
    g.restore();
    return w;
  }
  /* an index card with its push pin at the top-right corner (Zine.card pins the centre, over long titles) */
  function cardP(x, y, w, h, o = {}) { Z().card(x, y, w, h, { ...o, pin: false }); Z().pin(x + w / 2 - 34, y - h / 2 + 22); }
  const labelW = (id, size, text) => Z().measure((text || (window.Narration ? Narration.name(id) : id)).toUpperCase(), size) + size * 2.6;

  /* ======================================================================
     Passages: sentences laid out (wrapped) once, typed (typewriter) or written (crayon), highlighted by substring.
     layout(key, [text], x0, y0, maxW, size, lh, meas) → [{text, lines: [{str, off, x0, y}], size, meas}]
     ====================================================================== */
  const MEAS = { type: (s, size) => Z().measure(s, size), hand: (s, size) => tw(s, `${size}px ${HANDF}`) };
  function wrapBy(str, maxW, size, meas) {
    const words = String(str).split(/\s+/), out = []; let line = '';
    for (const w of words) { const t_ = line ? line + ' ' + w : w; if (meas(t_, size) > maxW && line) { out.push(line); line = w; } else line = t_; }
    if (line) out.push(line);
    return out;
  }
  const LAY = new Map();
  function layout(key, texts, x0, y0, maxW, size, lh, meas, gap = 0) {
    const k = [key, x0, y0, maxW, size, lh, gap].join('|');
    let L = LAY.get(k); if (L) return L;
    let y = y0;
    L = texts.map(text => {
      let off = 0;
      const lines = wrapBy(text, maxW, size, meas).map(str => { const o = { str, off, x0, y }; off += str.length + 1; y += lh; return o; });
      y += gap;
      return { text, lines, size, meas };
    });
    L.bottom = y;
    if (fontOk()) { if (LAY.size > 200) LAY.clear(); LAY.set(k, L); }
    return L;
  }
  const lenOf = s => s.text.length;
  function typeSent(s, t0, cps, o = {}) { s.lines.forEach(l => Z().type(l.str, l.x0, l.y, s.size, { t0: t0 + l.off / cps, cps, seed: (o.seed || 1) + l.off, color: o.color || INK })); }
  function handSent(s, t0, cps, o = {}) { s.lines.forEach((l, k) => hand(l.str, l.x0, l.y, s.size, t0 + l.off / cps, { dur: Math.max(0.2, l.str.length / cps), align: 'left', color: o.color || INK, seed: (o.seed || 1) + k })); }
  /* the spans of a substring over a laid-out sentence's lines → [{x, y, w}] */
  function spans(s, sub) {
    const a = sub == null ? 0 : s.text.indexOf(sub); if (a < 0) return [];
    const b = sub == null ? s.text.length : a + sub.length, out = [];
    for (const l of s.lines) {
      const la = Math.max(a, l.off), lb = Math.min(b, l.off + l.str.length); if (lb <= la) continue;
      out.push({ x: l.x0 + s.meas(l.str.slice(0, la - l.off), s.size), y: l.y, w: s.meas(l.str.slice(la - l.off, lb - l.off), s.size) });
    }
    return out;
  }
  function markSpans(sp, colour, t0, seed, size) { sp.forEach((q, k) => Z().highlight(q.x - 6, q.y + 2, q.w + 12, size * 1.15, colour, { t0: t0 + k * 0.2, dur: 0.3, seed: seed + k })); }
  const spanEnd = sp => { const q = sp[sp.length - 1]; return q ? { x: q.x + q.w, y: q.y } : null; };

  /* ======================================================================
     Staging: puppets, captions, chapter cards (after lessons/simile/levels/video.js)
     ====================================================================== */
  const POS = {
    two: { nia: { x: 250, y: 1088, h: 560, dip: 0 }, park: { x: 1665, y: 1088, h: 600, dip: 0 } },
    inset: { nia: { x: 124, y: 1098, h: 300, dip: 56 }, park: { x: 1796, y: 1098, h: 320, dip: 56 } },
  };
  function stage(t, part, keys) {
    const out = {};
    ['nia', 'park'].forEach(who => {
      const v = Object.assign({}, POS[keys[0][1]][who]);
      for (let i = 1; i < keys.length; i++) {
        const k = keys[i]; if (t <= k[0]) continue;
        const e = E.inOutCubic(P(t, k[0], k[0] + 0.7)), p = POS[k[1]][who];
        ['x', 'y', 'h', 'dip'].forEach(f => { v[f] = lerp(v[f], p[f], e); });
      }
      const talk = part.lines.filter(l => l.who === who).reduce((a, l) => Math.max(a, win(t, l.start - 0.35, l.end + 0.15, 0.3)), 0);
      v.y += v.dip * (1 - talk);
      out[who] = v;
    });
    return out;
  }
  const NIA_POINT = new Set(['s1_03', 's2_05', 's3_04', 's4_03', 's4_05', 's5_04']);
  const PARK_POINT = new Set(['s1_04', 's1_05', 's2_01', 's2_06', 's3_01', 's3_02', 's3_03', 's3_05', 's3_06', 's4_01', 's4_04', 's5_01', 's5_03']);
  function cast(t, ctx, st) {
    const cap = ctx.caption, id = cap ? cap.id : '', who = cap ? cap.who : '';
    const np = who === 'nia' && NIA_POINT.has(id) ? 'point' : null;
    const pp = who === 'park' && (PARK_POINT.has(id) || ctx.kind === 'reply') ? 'point' : null;
    Puppet.draw('nia', st.nia.x, st.nia.y, st.nia.h, { env: ctx.speaking('nia'), facing: 1, t, pose: np });
    Puppet.draw('park', st.park.x, st.park.y, st.park.h, { env: ctx.speaking('park'), facing: -1, t, pose: pp });
    placeCaption(ctx, st);
  }
  /* caption bubbles: ≤ 2 rows (wider first, then paged at sentence ends, timed by the words) */
  const BS = () => (Player.captionSize ? Player.captionSize() : 40);
  const pageCache = new Map();
  function pages(l, w) {
    const size = BS(), key = l.id + '|' + w + '|' + l.tokens.length + '|' + size + '|' + l.dur.toFixed(2);
    if (pageCache.has(key)) return pageCache.get(key);
    const toks = l.tokens, n = toks.length, maxW = w - 56;
    g.save(); g.font = `${size}px ${HANDF}`;
    const sp = g.measureText(' ').width, ws = toks.map(k => g.measureText(k.w).width);
    g.restore();
    const rowsOf = (a, b) => { let r = 1, rw = 0; for (let i = a; i < b; i++) { if (rw > 0 && rw + sp + ws[i] > maxW) { r++; rw = 0; } rw += ws[i] + sp; } return r; };
    const out = []; let a = 0;
    while (a < n) {
      let b = a + 1; while (b < n && rowsOf(a, b + 1) <= 2) b++;
      if (b < n) {
        const lo = a + Math.max(1, Math.floor((b - a) * 0.4));
        let cut_ = -1;
        for (const re of [/[.!?]["')]?$/, /[,;:]$/]) { for (let j = b; j >= lo; j--) if (re.test(toks[j - 1].w)) { cut_ = j; break; } if (cut_ > 0) break; }
        if (cut_ > 0) b = cut_;
      }
      out.push([a, b]); a = b;
    }
    const res = out.map(([a_, b_], k) => {
      const t0 = k === 0 ? 0 : Math.max(0, toks[a_].s - 0.15);
      return { t0, text: toks.slice(a_, b_).map(x => x.w).join(' '), rows: rowsOf(a_, b_), tokens: toks.slice(a_, b_).map(x => ({ w: x.w, s: x.s - t0, e: x.e - t0 })) };
    });
    if (pageCache.size > 300) pageCache.clear();
    if (fontOk()) pageCache.set(key, res);
    return res;
  }
  function fit(l, w0, maxW) {
    const BLH = BS() * 1.18;
    for (let w = w0; w <= maxW; w += 40) { const pg = pages(l, w); if (pg.length === 1) return { w, pages: pg, h: pg[0].rows * BLH + 40 }; }
    const pg = pages(l, w0);
    return { w: w0, pages: pg, h: Math.max(...pg.map(x => x.rows)) * BLH + 40 };
  }
  const pageAt = (pg, lt) => { let k = 0; for (let i = 1; i < pg.length; i++) if (lt >= pg[i].t0) k = i; return pg[k]; };
  function placeCaption(ctx, st) {
    const l = ctx.caption; if (!l) return;
    const p = st[l.who]; if (!p) return;
    const nia = l.who === 'nia', dir = nia ? 1 : -1, side = nia ? 'left' : 'right', k = BS() / 40;
    const tall = p.h > 420;
    const f = fit(l, Math.round(700 * k), Math.round(860 * k));
    const w = f.w, bh = f.h;
    let cx, cy;
    if (tall) { cx = nia ? p.x - 60 + w / 2 : p.x + 60 - w / 2; cy = 84 + bh / 2; }
    else { cx = p.x + dir * (0.2 * p.h + w / 2 + 16); cy = Math.min(876, 968 - bh / 2); }
    cx = Math.max(w / 2 + 16, Math.min(1904 - w / 2, cx));
    cy = Math.max(bh / 2 + 16, cy);
    const cap = { x: cx, y: cy, side, w };
    if (f.pages.length > 1) { const pg = pageAt(f.pages, ctx.t - l.start); Object.assign(cap, { text: pg.text, tokens: pg.tokens, t0: pg.t0 }); }
    ctx.captionAt = cap;
  }

  /* ---------- chapter cards: a photocopied zine page on the cork, a case-board chip, tears away ---------- */
  const CARD = { hold: 1.1, tear: 0.7, lead: 2.2 };
  const CH_CHIP = { s1: ['first-limited', 'AUTHOR vs NARRATOR'], s2: ['contradicts', 'CASE FILE #1'], s3: ['third-limited', 'POINT OF VIEW'], s4: ['missing-info', 'CASE FILE #2'], s5: ['says-vs-does', 'THE CHECKLIST'] };
  const cardW = new Map();
  function ransomFit(str, seed, maxW, max) {
    const k = str + '|' + seed; let w0 = cardW.get(k);
    if (w0 == null) { w0 = ransom(str, -5000, -5000, 100, 1e9, { seed, palette: COVER_PAL }); if (fontOk()) cardW.set(k, w0); }
    return Math.min(max, (maxW / Math.max(1, w0)) * 100);
  }
  function drawCard(i) {
    const seg = segs()[i]; if (!seg) return;
    const n = i + 1, Zn = Z();
    Zn.cork();
    Zn.page(960, 530, 1320, 820, { seed: 900 + n, rot: -0.012 + (n % 2) * 0.02, staple: 'left', tape: 1, dark: 0.55 });
    Zn.type('PAGE ' + n + ' OF ' + segs().length, 960, 250, 44, { align: 'center', seed: 910 + n });
    const title = String(seg.chapter || seg.id).toUpperCase();
    ransom(title, 960, 480, ransomFit(title, 950 + n, 1100, 124), -1e6, { seed: 950 + n, palette: COVER_PAL });
    const ch = CH_CHIP[seg.id];
    if (ch) Zn.label(ch[0], 960, 660, { size: 38, rot: -0.03, seed: 960 + n, text: ch[1] });
    Zn.type('~ ' + String((Game.lesson && Game.lesson.title) || '').toLowerCase() + ' ~', 960, 790, 32, { align: 'center', color: GREY, seed: 970 + n });
  }
  function chapterSfx(part) { return part.kind === 'lines' ? [{ name: 'tear', t: CARD.hold, gain: 0.5 }] : []; }

  const BODY = {};
  const MARKS = {};    // QA: which reply marks are on screen (per segment)

  /* ======================================================================
     s1 "Poe did it": Nia's photocopy of "The Tell-Tale Heart", her book report, the quote typed (1850 text),
     AUTHOR vs NARRATOR cards, red string from the quote to NARRATOR; reply: "I am" highlighted + tagged NARRATOR
     ====================================================================== */
  const S1 = { pw: 1400, ph: 300, author: { x: 690, y: 640, w: 470, h: 250 }, narr: { x: 1290, y: 640, w: 470, h: 250 }, rep: { w: 440, h: 290 } };
  const poeLay = () => layout('poe', [SCR().poe.sentences[0].text], -S1.pw / 2 + 60, -S1.ph / 2 + 150, S1.pw - 120, 44, 64, MEAS.type);
  function penDoodle(x, y, s, seed) {
    const o = { w: 5, color: INK, seed, passes: 1, step: 8 };
    ink([[x - 70 * s, y + 40 * s], [x + 40 * s, y - 60 * s]], o);
    ink([[x - 58 * s, y + 52 * s], [x + 52 * s, y - 48 * s]], { ...o, seed: seed + 1 });
    ink([[x + 40 * s, y - 60 * s], [x + 64 * s, y - 70 * s], [x + 52 * s, y - 48 * s]], { ...o, seed: seed + 2 });
    ink([[x - 70 * s, y + 40 * s], [x - 88 * s, y + 70 * s], [x - 58 * s, y + 52 * s]], { ...o, seed: seed + 3 });
    ink([[x - 110 * s, y + 80 * s], [x - 60 * s, y + 76 * s], [x - 30 * s, y + 84 * s]], { ...o, w: 3, seed: seed + 4 });
  }
  function mouthDoodle(x, y, s, seed) {
    const o = { w: 5, color: INK, seed, passes: 1, step: 8 };
    ink([[x - 60 * s, y], [x - 30 * s, y - 22 * s], [x - 8 * s, y - 14 * s], [x, y - 20 * s], [x + 8 * s, y - 14 * s], [x + 30 * s, y - 22 * s], [x + 60 * s, y]], o);
    ink([[x - 60 * s, y], [x - 20 * s, y + 30 * s], [x + 20 * s, y + 30 * s], [x + 60 * s, y]], { ...o, seed: seed + 1 });
    ink([[x - 58 * s, y], [x, y + 6 * s], [x + 58 * s, y]], { ...o, w: 3, seed: seed + 2 });
    for (let k = -1; k <= 1; k++) ink([[x + 78 * s, y + k * 22 * s], [x + 104 * s, y + k * 34 * s]], { ...o, w: 3, seed: seed + 5 + k });
  }
  BODY.s1 = function (t, part, ctx, c) {
    const Zn = Z(), poe = SCR().poe, src = poe.source, quote = poe.sentences[0].text, ans = ansOf(ctx, part);
    const tm = {
      page: part.kind === 'lines' ? CARD.hold + 0.3 : -1e6, rep: c.W('s1_01', 'report'), deeply: c.W('s1_01', 'deeply'),
      move: c.L('s1_03') - 0.3, quote: c.L('s1_03'), qEnd: c.W('s1_03', 'basically'),
      author: c.W('s1_04', 'Poe'), wrote: c.W('s1_04', 'wrote'), narr: c.W('s1_04', 'who'), string: c.W('s1_04', 'telling'),
      iam: branch(c, { q1_right: 'I', q1_wrong0: 'I', q1_wrong2: 'narrator' }),
      you: ans === 2 ? c.W('q1_wrong2', 'you') : 1e6, notPoe: ans === 0 ? c.W('q1_wrong0', 'wrote') : 1e6,
      neq: c.W('s1_05', 'not'), wrong: c.W('s1_06', 'wrong'),
    };
    tm.tag = tm.iam + 0.5;
    Zn.cork();
    // the photocopy: small in the middle while Nia talks, then up top (full size) for the quote
    const pl = track(t, [[0, 960, 470, 0.62, -0.012], [tm.move, 960, 250, 1, -0.004]], 0.7);
    const s = pl.s * pop(t, tm.page, 0.4, 1.6);
    const lay = poeLay(), qcps = cpsBetween(quote.length, tm.quote, tm.qEnd);
    if (s > 0.001) {
      g.save(); g.translate(pl.x, pl.y); g.rotate(pl.rot); g.scale(s, s);
      Zn.page(0, 0, S1.pw, S1.ph, { seed: 101, tape: 1, dark: 0.5 });
      Zn.type(String(src.work).toUpperCase(), -S1.pw / 2 + 60, -S1.ph / 2 + 56, 40, { seed: 102 });
      Zn.type(`${src.author}, ${POE_FIRST} · the ${src.year} text`, S1.pw / 2 - 60, -S1.ph / 2 + 60, 26, { align: 'right', color: GREY, seed: 103 });
      ink([[-S1.pw / 2 + 60, -S1.ph / 2 + 92], [S1.pw / 2 - 60, -S1.ph / 2 + 92]], { w: 2, color: 'rgba(20,18,16,0.5)', seed: 104, passes: 1, step: 80 });
      // until the quote types: toner bars (the page, too small to read)
      if (t < tm.quote) for (let k = 0; k < 3; k++) paperRect(-40 + (k === 2 ? -260 : 0), -S1.ph / 2 + 150 + k * 64, S1.pw - 200 - (k === 2 ? 520 : 0), 20, 'rgba(20,18,16,0.22)', { seed: 105 + k, depth: 0, boil: false });
      typeSent(lay[0], tm.quote, qcps, { seed: 106 });
      // the reply: "I am" in the narrator's highlighter; the reader's "you" (option 2)
      markSpans(spans(lay[0], 'I am'), '#fff04a', tm.iam, 110, 44);
      markSpans(spans(lay[0], 'you'), '#52c8ff', tm.you, 112, 44);
      g.restore();
    }
    const toG = q => ({ x: pl.x + q.x * pl.s, y: pl.y + q.y * pl.s });
    const iam = spans(lay[0], 'I am')[0];
    if (t > tm.quote) note('poe', [`"${quote}"`, `(${src.author}, "${src.work}", the ${src.year} text)`]);
    // Nia's book report: under the page, then to the left edge
    const rp = track(t, [[0, 960, 815, 0.9, -0.03], [tm.move, 250, 600, 0.66, -0.05]], 0.7);
    const rs = rp.s * pop(t, tm.rep, 0.35, 1.8);
    if (rs > 0.001) {
      const R = S1.rep;
      g.save(); g.translate(rp.x, rp.y); g.rotate(rp.rot); g.scale(rs, rs);
      cardP(0, 0, R.w, R.h, { seed: 121, title: 'BOOK REPORT', t0: tm.rep });
      hand('by Nia B.', R.w / 2 - 62, -R.h / 2 + 34, 28, tm.rep + 0.2, { align: 'right', color: GREY, seed: 122, dur: 0.3 });
      hand('Poe was a deeply', -R.w / 2 + 34, -R.h / 2 + 132, 38, tm.deeply, { align: 'left', color: '#2b4a8c', seed: 123, dur: 0.5 });
      hand('nervous man.', -R.w / 2 + 34, -R.h / 2 + 180, 38, tm.deeply + 0.4, { align: 'left', color: '#2b4a8c', seed: 124, dur: 0.4 });
      if (T > tm.wrong) {
        const pw_ = tw('Poe', `38px ${HANDF}`);
        strike(-R.w / 2 + 28, -R.h / 2 + 136, -R.w / 2 + 40 + pw_, -R.h / 2 + 128, P(T, tm.wrong, tm.wrong + 0.3), 125, RED, 6);
        hand('the narrator', -R.w / 2 + 30, -R.h / 2 + 90, 34, tm.wrong + 0.3, { align: 'left', color: RED, seed: 126, dur: 0.4, rot: -0.04 });
      }
      g.restore();
      note('report', ["Nia's book report: Poe was a deeply nervous man."].concat(t > tm.wrong ? ['Correction: not Poe. The narrator.'] : []));
    }
    // AUTHOR and NARRATOR, pinned apart (s1_04)
    const A = S1.author, Nn = S1.narr;
    const sa = pop(t, tm.author, 0.35, 1.8);
    if (sa > 0) {
      g.save(); g.translate(A.x, A.y); g.rotate(-0.02); g.scale(sa, sa);
      cardP(0, 0, A.w, A.h, { seed: 131, title: 'AUTHOR', t0: tm.author });
      penDoodle(-A.w / 2 + 110, 30, 0.8, 132);
      Zn.type('Poe: wrote it', -A.w / 2 + 200, 40, 34, { t0: tm.wrote, cps: 30, seed: 133 });
      if (T > tm.notPoe) inkCircle(-A.w / 2 + 300, 40, 120, { sx: 1.1, sy: 0.4, w: 5, color: RED, seed: 134, prog: P(T, tm.notPoe, tm.notPoe + 0.4) });
      g.restore();
      note('author', ['AUTHOR (Poe): wrote it.']);
    }
    const sn = pop(t, tm.narr, 0.35, 1.8);
    if (sn > 0) {
      g.save(); g.translate(Nn.x, Nn.y); g.rotate(0.02); g.scale(sn, sn);
      cardP(0, 0, Nn.w, Nn.h, { seed: 141, title: 'NARRATOR', t0: tm.narr });
      mouthDoodle(-Nn.w / 2 + 100, 40, 0.8, 142);
      Zn.type('the "I": tells it', -Nn.w / 2 + 200, 40, 32, { t0: tm.narr + 0.3, cps: 30, seed: 143 });
      g.restore();
      note('narrator', ['NARRATOR (the "I"): tells it.']);
    }
    // red string: from the quote's "I am" to NARRATOR (never to AUTHOR)
    if (iam && t > tm.string) {
      const a = toG({ x: iam.x + iam.w / 2, y: iam.y + 30 });
      Zn.string(a.x, a.y, Nn.x - 10, Nn.y - Nn.h / 2 + 20, { t0: tm.string, dur: 0.6, sag: 50 });
    }
    // the reply: tag NARRATOR under the highlight
    if (iam && t > tm.tag) {
      const a = toG({ x: iam.x + iam.w / 2, y: iam.y });
      chip('NARRATOR', a.x + 150, a.y + 78, '#fff04a', { size: 28, rot: 0.04, s: pop(t, tm.tag, 0.3, 1.8), seed: 151 });
      note('iam', ['"I am": the narrator speaking, not Poe.']);
    }
    MARKS.s1 = { iam: t > tm.iam + 0.4, tag: t > tm.tag + 0.3 };
    // after: the author is not the narrator (s1_05)
    if (t > tm.neq) {
      const x = (A.x + Nn.x) / 2, y = A.y + 10, pr = P(t, tm.neq, tm.neq + 0.3);
      ink([[x - 40, y - 16], [x + 40, y - 16]], { w: 9, color: INK, seed: 161, prog: pr });
      ink([[x - 40, y + 16], [x + 40, y + 16]], { w: 9, color: INK, seed: 162, prog: pr });
      if (t > tm.neq + 0.3) ink([[x + 26, y - 50], [x - 26, y + 50]], { w: 9, color: RED, seed: 163, prog: P(t, tm.neq + 0.3, tm.neq + 0.55) });
      note('neq', ['The author is not the narrator.']);
    }
    const st = stage(t, part, part.kind === 'lines' ? [[0, 'two'], [tm.move, 'inset']] : [[0, 'inset']]);
    cast(t, ctx, st);
  };
  S.s1 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 1.6, after: 1.8 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      if (part.kind === 'lines') out.push({ name: 'paper', t: CARD.hold + 0.3, gain: 0.3 });
      out.push({ name: 'pop', t: c.W('s1_01', 'report'), gain: 0.25 }, { name: 'scribble', t: c.W('s1_01', 'deeply'), gain: 0.25 });
      out.push({ name: 'whoosh', t: c.L('s1_03') - 0.3, gain: 0.25 }, { name: fx('typewriter', 'scribble'), t: c.L('s1_03'), gain: 0.3 });
      out.push({ name: 'paper', t: c.W('s1_04', 'Poe'), gain: 0.3 }, { name: 'paper', t: c.W('s1_04', 'who'), gain: 0.3 }, { name: fx('string_pull', 'whoosh'), t: c.W('s1_04', 'telling'), gain: 0.25 });
      out.push({ name: 'scribble', t: branch(c, { q1_right: 'I', q1_wrong0: 'I', q1_wrong2: 'narrator' }), gain: 0.3 }, { name: 'pop', t: branch(c, { q1_right: 'I', q1_wrong0: 'I', q1_wrong2: 'narrator' }) + 0.5, gain: 0.3 });
      out.push({ name: 'scribble', t: c.W('s1_05', 'not'), gain: 0.3 }, { name: 'scribble', t: c.W('s1_06', 'wrong'), gain: 0.3 });
      return out;
    },
  };

  /* ======================================================================
     s2 "The volcano": the MY VOLCANO case file typed sentence by sentence as Ms. Park reads; the volcano in its foam,
     the empty vinegar bottle, Pepper asleep upstairs; reply: red strings from the evidence sentences to their clue
     tags (Narration.reliability), UNRELIABLE stamped on the card
     ====================================================================== */
  const S2 = { card: { x: 650, y: 430, w: 1060, h: 660 }, tagX: 1560 };
  const s2Lay = () => { const K = S2.card, top = K.y - K.h / 2 + 70; return layout('volcano', SCR().volcano.sentences.map(s => s.text), K.x - K.w / 2 + 80, top + 46 * 2 - 18, K.w - 130, 30, 46, MEAS.type); };
  BODY.s2 = function (t, part, ctx, c) {
    const Zn = Z(), V = SCR().volcano, rel = Narration.reliability(V), K = S2.card, lay = s2Lay();
    const lines_ = part.kind === 'lines';
    const tm = {
      card: c.W('s2_01', 'Volcano'), sent: [c.W('s2_02', 'My'), c.W('s2_02', 'Then'), c.L('s2_03'), c.L('s2_04'), c.W('s2_04', 'Anyway')],
      ends: [c.W('s2_02', 'Then'), c.E('s2_02'), c.E('s2_03'), c.W('s2_04', 'Anyway'), c.E('s2_04')],
      knock: c.W('s2_02', 'knocked'), vinegar: c.W('s2_03', 'vinegar'), pepper: c.W('s2_04', 'upstairs'),
      clue: {
        'others-react': branch(c, { q2_right: 'Mom', q2_wrong0: ['upstairs', 1, 0.3], q2_wrong1: ['upstairs', 1, 0.3] }),
        contradicts: branch(c, { q2_right: 'asleep', q2_wrong0: 'asleep', q2_wrong1: 'asleep' }),
        'something-to-gain': branch(c, { q2_right: 'extension', q2_wrong0: 'extension', q2_wrong1: 'extension' }),
      },
      stamp: branch(c, { q2_right: 'add', q2_wrong0: 'add', q2_wrong1: 'trust' }),
      unrel: c.W('s2_06', 'unreliable'), framed: c.W('s2_07', 'framed'),
    };
    Zn.cork();
    // the case file
    const sc = pop(t, tm.card, 0.4, 1.6);
    if (sc > 0) {
      g.save(); g.translate(K.x, K.y); g.scale(sc, sc); g.translate(-K.x, -K.y);
      cardP(K.x, K.y, K.w, K.h, { seed: 201, title: String(V.title).toUpperCase(), t0: tm.card });
      const top = K.y - K.h / 2 + 70;
      Zn.type(`a story written for this lesson · told by ${V.cast[Narration.narrator(V)]}`, K.x - K.w / 2 + 80, top + 28, 24, { t0: tm.card + 0.3, cps: 50, color: GREY, seed: 202 });
      lay.forEach((s, i) => {
        if (t < tm.sent[i]) return;
        Zn.type(String(i + 1), K.x - K.w / 2 + 44, s.lines[0].y, 26, { color: RED, seed: 210 + i });
        typeSent(s, tm.sent[i], cpsBetween(lenOf(s), tm.sent[i], tm.ends[i]), { seed: 220 + i * 10 });
      });
      // the reply: each evidence sentence in its clue's highlighter
      rel.evidence.forEach((e, k) => markSpans(spans(lay[e.i]), COL(e.clue), tm.clue[e.clue] ?? 1e6, 240 + k * 3, 30));
      g.restore();
      if (lines_) note('volcano', [`${V.title} (a story written for this lesson), told by ${V.cast[V.teller]}:`].concat(lay.filter((s, i) => t > tm.sent[i]).map((s, i) => `${i + 1}. ${s.text}`)));
    }
    // the props: big in the right column while the story is read, small in the top corner for the reply
    const small = lines_ ? 1e6 : -1e6;
    const pp = track(t, [[0, 1560, 245, 1], [small, 1735, 118, 0.52]], 0.6);
    const vp = track(t, [[0, 1600, 745, 1], [small, 1470, 250, 0.5]], 0.6);
    const bp = track(t, [[0, 1330, 745, 1], [small, 1335, 250, 0.5]], 0.6);
    prop('volcano', vp.x, vp.y, 300, { s: vp.s * pop(t, tm.knock, 0.35, 2), seed: 251 });
    if (lines_ && t > tm.knock) note('props', ['(the volcano, in a puddle of foam)'].concat(t > tm.vinegar ? ['(the empty vinegar bottle)'] : [], t > tm.pepper ? ['(Pepper, asleep upstairs)'] : []));
    prop('vinegar_bottle', bp.x, bp.y, 230, { s: bp.s * pop(t, tm.vinegar, 0.35, 2), rot: -0.08, seed: 252 });
    const ps = pp.s * pop(t, tm.pepper, 0.35, 1.8);
    if (ps > 0.001) {
      g.save(); g.translate(pp.x, pp.y); g.rotate(0.02); g.scale(ps, ps);
      Zn.page(0, 0, 520, 300, { seed: 261, tape: 1, dark: 0.35 });
      Zn.type('UPSTAIRS · all afternoon', -230, -112, 26, { color: GREY, seed: 262 });
      prop('dog', 10, 110, 150, { seed: 263 });
      for (let k = 0; k < 3; k++) { const u = ((T * 0.35 + k / 3) % 1); hand('z', 150 + u * 60 + k * 8, 10 - u * 90, 30 + k * 8, -1e6, { color: INK, alpha: Math.sin(u * Math.PI), seed: 264 + k, dur: 0.01 }); }
      g.restore();
      if (t > tm.framed) sticky(['framed!'], pp.x, pp.y + 150 * pp.s + 50, tm.framed, { w: 200, size: 44, seed: 267, rot: -0.05, color: WHITE, ink: RED });
      if (t > tm.framed) note('framed', ['Pepper: framed.']);
    }
    // the reply: red strings from the evidence sentences to their clue tags; the verdict stamp
    const tags = [];
    rel.evidence.forEach(e => {
      const t0 = tm.clue[e.clue] ?? 1e6; if (t < t0) return;
      const end = spanEnd(spans(lay[e.i])), prevY = tags.length ? tags[tags.length - 1].y : -1e9;
      const y = Math.max(end.y, prevY + 84), x = S2.tagX, w = labelW(e.clue, 28);
      Zn.string(end.x + 18, end.y, x - w / 2 + 10, y, { t0: t0 + 0.25, dur: 0.4, sag: 30 });
      tags.push({ y, draw: () => Zn.label(e.clue, x, y, { size: 28, rot: ((e.i % 2) - 0.5) * 0.06, s: pop(t, t0 + 0.55, 0.3, 1.8), seed: 270 + e.i }) });
    });
    tags.forEach(q => q.draw());
    if (tags.length) note('clues', rel.evidence.filter(e => t > (tm.clue[e.clue] ?? 1e6)).map(e => `${Narration.CLUES[e.clue].name}: "${V.sentences[e.i].text}"`));
    const verdict = rel.reliable ? 'RELIABLE' : 'UNRELIABLE';
    stampMark(verdict, K.x + 180, K.y + 250, stampS(t, tm.stamp) * 0.95, { rot: -0.13, size: 76 });
    if (t > tm.stamp) note('verdict', [`${V.cast[V.teller]}: ${verdict} narrator.`]);
    MARKS.s2 = { strings: rel.evidence.every(e => t > (tm.clue[e.clue] ?? 1e6) + 0.9), stamp: t > tm.stamp + 0.3, n: rel.evidence.length };
    // after: what an unreliable narrator is (s2_06)
    if (t > tm.unrel) {
      sticky(['unreliable narrator:', "can't take his word for it,", 'so read the clues'], K.x - K.w / 2 + 250, K.y + 250, tm.unrel, { w: 430, size: 32, seed: 281, rot: -0.04 });
      note('unrel', ["Unreliable narrator: you can't take his word for it, so you read the clues."]);
    }
    const st = stage(t, part, lines_ ? [[0, 'two'], [c.L('s2_01') + 0.4, 'inset']] : [[0, 'inset']]);
    cast(t, ctx, st);
  };
  S.s2 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 1.8, after: 1.8 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: 'paper', t: c.W('s2_01', 'Volcano'), gain: 0.3 });
      [c.W('s2_02', 'My'), c.W('s2_02', 'Then'), c.L('s2_03'), c.L('s2_04'), c.W('s2_04', 'Anyway')].forEach(t => out.push({ name: fx('typewriter', 'scribble'), t, gain: 0.22 }));
      out.push({ name: 'thunk', t: c.W('s2_02', 'knocked'), gain: 0.5 }, { name: 'pop', t: c.W('s2_03', 'vinegar'), gain: 0.25 }, { name: 'pop', t: c.W('s2_04', 'upstairs'), gain: 0.25 });
      for (const tb of [{ q2_right: 'Mom', q2_wrong0: ['upstairs', 1, 0.3], q2_wrong1: ['upstairs', 1, 0.3] }, { q2_right: 'asleep', q2_wrong0: 'asleep', q2_wrong1: 'asleep' }, { q2_right: 'extension', q2_wrong0: 'extension', q2_wrong1: 'extension' }]) {
        const t0 = branch(c, tb); out.push({ name: 'scribble', t: t0, gain: 0.22 }, { name: fx('string_pull', 'whoosh'), t: t0 + 0.25, gain: 0.2 }, { name: 'pop', t: t0 + 0.55, gain: 0.25 });
      }
      out.push({ name: fx('stamp_thump', 'stamp'), t: branch(c, { q2_right: 'add', q2_wrong0: 'add', q2_wrong1: 'trust' }), gain: 0.6 });
      out.push({ name: 'pop', t: c.W('s2_06', 'unreliable'), gain: 0.3 }, { name: 'scribble', t: c.W('s2_07', 'framed'), gain: 0.3 });
      return out;
    },
  };

  /* ======================================================================
     s3 "Three cameras": three typed strips with their POV chip (Narration.pov label) and one eye per head the
     narrator enters (pov().heads); the q3 strip's eyes open in the reply; after: POINT OF VIEW vs PERSPECTIVE
     ====================================================================== */
  const S3 = { rows: [{ key: 'cameraFirst', y: 150, h: 160 }, { key: 'cameraThird', y: 335, h: 160 }, { key: 'q3', y: 572, h: 270 }], eyeX: 1560, eyeDX: 118 };
  const rowLay = (r, joined) => { const p = SCR()[r.key]; return layout('row_' + r.key, joined ? [p.sentences.map(s => s.text).join(' ')] : p.sentences.map(s => s.text), 110, r.y - r.h / 2 + 96, 1330, 36, 54, MEAS.type); };
  const HEAD_WORD = { marcus: 'Marcus', pepper: 'dog', mom: 'Mom' };   // the reply names the heads in these words
  BODY.s3 = function (t, part, ctx, c) {
    const Zn = Z(), SC = SCR();
    const tm = {
      rows: [
        { chip: c.W('s3_01', 'first'), text: c.W('s3_01', 'Marcus'), textEnd: c.W('s3_01', 'first'), eye: c.W('s3_01', 'head') },
        { chip: c.W('s3_02', 'third'), text: c.W('s3_02', 'Marcus'), textEnd: c.W('s3_02', 'third'), eye: c.W('s3_02', 'one') },
        { chip: 1e6, text: c.W('s3_03', 'Marcus'), textEnd: c.E('s3_03'), eye: 1e6, sent: [c.W('s3_03', 'Marcus'), c.W('s3_03', 'Upstairs'), c.W('s3_03', 'kitchen') - 0.25] },
      ],
      label: branch(c, { q3_right: 'omniscient', q3_wrong0: 'omniscient', q3_wrong2: 'omniscient' }),
      head: {},
      same: c.W('s3_04', 'same'), nope: c.W('s3_05', 'Nope'), position: c.W('s3_05', 'position'), attitude: c.W('s3_06', 'attitude'), marcus: c.W('s3_06', 'Marcus'),
      cards: c.L('s3_04') - 0.2,
    };
    for (const h in HEAD_WORD) tm.head[h] = branch(c, { q3_right: HEAD_WORD[h], q3_wrong0: HEAD_WORD[h], q3_wrong2: HEAD_WORD[h] });
    Zn.cork();
    const after = part.kind === 'after';
    let allOpen = true;
    if (!after) S3.rows.forEach((r, ri) => {
      const p = SC[r.key], v = Narration.pov(p), rt = tm.rows[ri], q3 = r.key === 'q3';
      const s = pop(t, rt.text - 0.2, 0.35, 1.6);
      if (s <= 0) { if (q3) allOpen = false; return; }
      const lay = rowLay(r, !q3);
      g.save(); g.translate(960, r.y); g.scale(s, s); g.translate(-960, -r.y);
      Zn.page(960, r.y, 1800, r.h, { seed: 301 + ri, tape: 1, dark: 0.4, rot: 0 });
      // the chip: the model's point of view (q3: only in the reply)
      const ct = q3 ? tm.label : rt.chip;
      if (t > ct) {
        const w = labelW(v.id, 26, v.label);
        Zn.label(v.id, 90 + w / 2, r.y - r.h / 2 + 40, { size: 26, rot: -0.01, s: pop(t, ct, 0.3, 1.8), seed: 311 + ri, text: v.label.toUpperCase() });
      } else if (q3) hand('which point of view?', 100, r.y - r.h / 2 + 42, 34, rt.text, { align: 'left', color: GREY, seed: 314, dur: 0.4 });
      if (q3) lay.forEach((sn, i) => {
        const t0 = rt.sent[i], nx = i + 1 < lay.length ? rt.sent[i + 1] : rt.textEnd;
        typeSent(sn, t0, cpsBetween(lenOf(sn), t0, nx), { seed: 320 + i * 10 });
        // the reply: the sentence that enters a head, in that head's colour
        (p.sentences[i].thoughts || []).forEach(h => markSpans(spans(sn), HEAD_COL[h] || '#fff04a', tm.head[h] ?? 1e6, 330 + i, 36));
      });
      else typeSent(lay[0], rt.text, cpsBetween(lenOf(lay[0]), rt.text, rt.textEnd), { seed: 316 + ri });
      // one eye per head the narrator can see into (pov().heads)
      v.heads.forEach((h, k) => {
        const te = q3 ? tm.head[h] ?? 1e6 : rt.eye;
        if (q3 && t < te + 0.5) allOpen = false;
        if (t < te) return;
        const x = S3.eyeX + k * S3.eyeDX, y = r.y - (q3 ? 20 : 12), es = pop(t, te, 0.3, 1.8);
        g.save(); g.translate(x, y); g.scale(es, es);
        Zn.eye(0, 0, 44, { open: E.outQuad(P(t, te + 0.1, te + 0.5)), fill: HEAD_COL[h] || '#52c8ff', seed: 340 + ri * 5 + k, label: Narration.castName(p, h) });
        g.restore();
      });
      g.restore();
      const txt_ = p.sentences.map(x => x.text).join(' ');
      if (t > rt.text && (q3 || part.kind === 'lines')) note('row' + ri, [(q3 ? (t > tm.label ? v.label : 'Which point of view?') : v.label) + `: "${txt_}"`].concat(t > (q3 ? tm.label : rt.eye) ? [`Heads it can see into: ${v.heads.map(h => Narration.castName(p, h)).join(', ')}`] : []));
    });
    // the reply's stamp: the model's label of the q3 passage
    const v3 = Narration.pov(SC.q3);
    if (!after) stampMark(v3.label.toUpperCase(), 1250, 712, stampS(t, tm.label + 0.35) * 0.9, { rot: -0.06, size: 50 });
    MARKS.s3 = { eyes: allOpen && t > tm.label, label: t > tm.label + 0.6, heads: v3.heads.length };
    // after: POINT OF VIEW (position) vs PERSPECTIVE (attitude)
    if (after) {
      const cards = [
        { x: 705, title: 'POINT OF VIEW', big: '= position', sub: ['who tells it: I or he/she,', 'one head or all'], t0: tm.cards, t1: tm.position, seed: 361 },
        { x: 1215, title: 'PERSPECTIVE', big: '= attitude', sub: ['how they feel about it'], t0: tm.cards + 0.25, t1: tm.attitude, seed: 371 },
      ];
      cards.forEach(k => {
        const s = pop(t, k.t0, 0.35, 1.8); if (s <= 0) return;
        g.save(); g.translate(k.x, 520); g.rotate(k.x < 960 ? -0.02 : 0.02); g.scale(s, s);
        cardP(0, 0, 470, 400, { seed: k.seed, title: k.title, t0: k.t0 });
        Zn.type(k.big, -200, -60, 46, { t0: k.t1, cps: 30, seed: k.seed + 1 });
        k.sub.forEach((l, i) => Zn.type(l, -200, 20 + i * 46, 27, { t0: k.t1 + 0.4 + i * 0.4, cps: 45, color: INK, seed: k.seed + 2 + i }));
        g.restore();
      });
      // Nia's "the same thing": an =, crossed out at "Nope"
      if (t > tm.same) {
        const pr = P(t, tm.same, tm.same + 0.3);
        ink([[932, 500], [988, 500]], { w: 9, color: INK, seed: 381, prog: pr }); ink([[932, 530], [988, 530]], { w: 9, color: INK, seed: 382, prog: pr });
        if (t > tm.nope) ink([[982, 460], [938, 570]], { w: 9, color: RED, seed: 383, prog: P(t, tm.nope, tm.nope + 0.25) });
      }
      if (t > tm.marcus) sticky(["Marcus's perspective:", 'the dog did it, and', "he's the victim"], 1215, 830, tm.marcus, { w: 390, size: 32, seed: 391, rot: 0.03 });
      note('povp', ['POINT OF VIEW = position: who tells it (I or he/she), one head or all.'].concat(t > tm.attitude ? ['PERSPECTIVE = attitude: how they feel about it.'] : [], t > tm.marcus ? ["Marcus's perspective: the dog did it, and he's the victim."] : []));
    }
    const st = stage(t, part, after ? [[0, 'two']] : [[0, 'inset']]);
    cast(t, ctx, st);
  };
  S.s3 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 1.8, after: 1.8 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: fx('typewriter', 'scribble'), t: c.W('s3_01', 'Marcus'), gain: 0.25 }, { name: 'pop', t: c.W('s3_01', 'first'), gain: 0.25 }, { name: 'sparkle', t: c.W('s3_01', 'head'), gain: 0.2 });
      out.push({ name: fx('typewriter', 'scribble'), t: c.W('s3_02', 'Marcus'), gain: 0.25 }, { name: 'pop', t: c.W('s3_02', 'third'), gain: 0.25 }, { name: 'sparkle', t: c.W('s3_02', 'one'), gain: 0.2 });
      out.push({ name: fx('typewriter', 'scribble'), t: c.W('s3_03', 'Marcus'), gain: 0.25 });
      for (const h in HEAD_WORD) out.push({ name: 'sparkle', t: branch(c, { q3_right: HEAD_WORD[h], q3_wrong0: HEAD_WORD[h], q3_wrong2: HEAD_WORD[h] }), gain: 0.22 });
      const lab = branch(c, { q3_right: 'omniscient', q3_wrong0: 'omniscient', q3_wrong2: 'omniscient' });
      out.push({ name: 'pop', t: lab, gain: 0.3 }, { name: fx('stamp_thump', 'stamp'), t: lab + 0.35, gain: 0.5 });
      out.push({ name: 'paper', t: c.L('s3_04') - 0.2, gain: 0.3 }, { name: 'scribble', t: c.W('s3_05', 'Nope'), gain: 0.3 }, { name: 'pop', t: c.W('s3_06', 'Marcus'), gain: 0.25 });
      return out;
    },
  };

  /* ======================================================================
     s4 "Not a liar": BEA'S VERSION in crayon; a linocut Bea; reply: red strings from "magic trick" / "foam dragon"
     to MISSING INFO (reliability(bea)), the note; after: Florida's reasons (N1), Marcus and Bea pinned to theirs
     ====================================================================== */
  const S4 = { card: { x: 560, y: 330, w: 900, h: 460 }, reasons: { x: 1500, y: 470, w: 600, h: 380 } };
  const CRAYON = ['#d9352c', '#2b4a8c', '#3c8a5a', '#e08a1e', '#7b4fd0'];
  const BEA_SPAN = { 0: 'magic trick', 1: 'foam dragon' };   // the words the storyboard strings from, per sentence
  const s4Lay = () => { const K = S4.card; return layout('bea', SCR().bea.sentences.map(s => s.text), K.x - K.w / 2 + 60, K.y - K.h / 2 + 150, K.w - 110, 46, 58, MEAS.hand); };
  BODY.s4 = function (t, part, ctx, c) {
    const Zn = Z(), B = SCR().bea, rel = Narration.reliability(B), K = S4.card, lay = s4Lay();
    const lines_ = part.kind === 'lines', after = part.kind === 'after';
    const tm = {
      bea: c.W('s4_01', 'Bea'), move: c.L('s4_02') - 0.3, card: c.L('s4_02') - 0.2,
      sent: [c.L('s4_02'), c.W('s4_02', 'He')], ends: [c.W('s4_02', 'He'), c.E('s4_02')],
      hi: [branch(c, { q4_right: 'truth', q4_wrong0: 'know', q4_wrong2: 'Bea' }), branch(c, { q4_right: 'dragon', q4_wrong0: 'honest', q4_wrong2: 'honest' })],
      tag: branch(c, { q4_right: 'missing', q4_wrong0: 'missing', q4_wrong2: 'missing' }),
      reasons: c.W('s4_04', 'Florida'), ticks: { dishonest: c.W('s4_04', 'lying'), 'missing-info': c.W('s4_04', 'missing'), 'self-deceiving': c.W('s4_04', 'fooling') },
      who: { marcus: c.W('s4_05', 'Marcus'), bea: c.W('s4_05', 'Bea'), pepper: c.W('s4_05', 'Pepper') },
    };
    tm.note = tm.tag + 0.7;
    Zn.cork();
    // Bea: centre stage while Ms. Park introduces her, then the right column; fades for Florida's card
    const bp = track(t, [[0, 960, 760, 1], [lines_ ? tm.move : -1e6, 1500, 720, 1]], 0.7);
    const ba = after ? 1 - P(t, 0, 0.4) : 1;
    alpha(ba, () => {
      prop('bea', bp.x, bp.y, 400, { s: pop(t, lines_ ? tm.bea : -1e6, 0.35, 1.8), seed: 401 });
      if (t > tm.bea) hand('Bea, 5', bp.x - 170, bp.y - 230, 44, tm.bea + 0.2, { color: '#7b4fd0', seed: 402, dur: 0.3, rot: -0.08 });
    });
    if (t > tm.bea) note('bea', ["Bea, Marcus's sister. She's five."]);
    // BEA'S VERSION, in crayon
    const sc = pop(t, tm.card, 0.4, 1.6);
    if (sc > 0) {
      g.save(); g.translate(K.x, K.y); g.scale(sc, sc); g.translate(-K.x, -K.y);
      cardP(K.x, K.y, K.w, K.h, { seed: 411, t0: tm.card, color: '#fffdf2' });
      const title = `${String(B.title).toUpperCase()}`;
      let x = K.x - K.w / 2 + 40;
      [...title].forEach((ch, i) => { hand(ch, x, K.y - K.h / 2 + 42, 50, tm.card + i * 0.04, { align: 'left', color: CRAYON[i % CRAYON.length], seed: 412 + i, dur: 0.1, rot: (hash1(i * 7) - 0.5) * 0.2 }); x += tw(ch, `50px ${HANDF}`) + 2; });
      Zn.type('a story written for this lesson · Bea is five', K.x - K.w / 2 + 60, K.y - K.h / 2 + 96, 22, { t0: tm.card + 0.3, cps: 50, color: GREY, seed: 430 });
      lay.forEach((s, i) => {
        if (t < tm.sent[i]) return;
        handSent(s, tm.sent[i], cpsBetween(lenOf(s), tm.sent[i], tm.ends[i]), { color: i ? '#c2452d' : '#5b3fb0', seed: 440 + i * 5 });
      });
      rel.evidence.forEach((e, k) => markSpans(spans(lay[e.i], BEA_SPAN[e.i]), COL(e.clue), tm.hi[k] ?? tm.tag, 450 + k * 3, 46));
      g.restore();
      if (lines_) note('beacase', [`${B.title} (a story written for this lesson; Bea is five):`].concat(lay.filter((s, i) => t > tm.sent[i]).map(s => s.text)));
    }
    // the reply: a red string from each highlight to its clue tag (the first sentence's tag to the right of the card,
    // the string arcing over the rest of its line; the last one's in the card's empty lower rows, straight down)
    const clues = [...new Set(rel.evidence.map(e => e.clue))];
    const tags = [];
    rel.evidence.forEach((e, k) => {
      const sp = spans(lay[e.i], BEA_SPAN[e.i]), q = sp[sp.length - 1] || spans(lay[e.i])[0];
      const w = labelW(e.clue, 30), first = k === 0 && rel.evidence.length > 1;
      const a = first ? { x: K.x + K.w / 2 + w / 2 - 20, y: q.y - 34 } : { x: q.x + q.w + w / 2 + 60, y: lay.bottom + 34 };
      const t0 = Math.max(tm.hi[k] ?? tm.tag, tm.tag) + 0.25;
      if (t > t0) {
        if (first) Zn.string(q.x + q.w, q.y - 22, a.x - w / 2 + 12, a.y, { t0, dur: 0.4, sag: -24 });
        else Zn.string(q.x + q.w / 2, q.y + 24, a.x - w / 2 + 12, a.y, { t0, dur: 0.4, sag: 16 });
      }
      tags.push({ a, id: e.clue, seed: 461 + k });
    });
    if (t > tm.tag) tags.forEach(q => Zn.label(q.id, q.a.x, q.a.y, { size: 30, rot: 0.03, s: pop(t, tm.tag, 0.3, 1.8), seed: q.seed }));
    if (t > tm.tag) note('beatag', clues.map(id => `Clue: ${Narration.CLUES[id].name}`));
    const kind = rel.reliable ? null : Narration.KINDS[rel.kind];
    if (kind && t > tm.note) {
      slip('honest + missing information = still unreliable', 620, 640, 30, tm.note, { seed: 471, cps: 60, rot: -0.01 });
      note('beanote', ['Honest + missing information = still unreliable.']);
    }
    MARKS.s4 = { tag: t > tm.tag + 0.3, strings: t > Math.max(tm.tag, ...rel.evidence.map((e, k) => tm.hi[k] ?? tm.tag)) + 0.7, note: t > tm.note + 0.8 };
    // after: Florida's reasons; Marcus and Bea pinned to their kind (the model's why), Pepper innocent
    if (after && t > tm.reasons) {
      const R = S4.reasons, s = pop(t, tm.reasons, 0.35, 1.7);
      g.save(); g.translate(R.x, R.y); g.rotate(0.015); g.scale(s, s);
      cardP(0, 0, R.w, R.h, { seed: 481, title: "FLORIDA'S REASONS", t0: tm.reasons });
      Zn.type('why a narrator can be unreliable', -R.w / 2 + 30, -R.h / 2 + 96, 22, { color: GREY, seed: 482, t0: tm.reasons + 0.2, cps: 50 });
      const who = { [SCR().volcano.why]: ['Marcus', tm.who.marcus], [B.why]: ['Bea', tm.who.bea] };
      REASONS.forEach(([id, label], i) => {
        const y = -R.h / 2 + 160 + i * 64, tk = tm.ticks[id];
        g.strokeStyle = INK; g.lineWidth = 3; g.strokeRect(-R.w / 2 + 34, y - 16, 32, 32);
        if (T > tk) ink([[-R.w / 2 + 38, y], [-R.w / 2 + 48, y + 12], [-R.w / 2 + 70, y - 22]], { w: 5, color: RED, seed: 483 + i, prog: P(T, tk, tk + 0.25), passes: 1 });
        Zn.type(label, -R.w / 2 + 86, y, 30, { t0: tm.reasons + 0.3 + i * 0.3, cps: 40, seed: 486 + i });
        const w_ = who[id];
        if (w_ && T > w_[1]) chip(w_[0], R.w / 2 - 90, y, id === B.why ? COL('missing-info') : COL('something-to-gain'), { size: 26, rot: -0.05, s: pop(T, w_[1], 0.3, 1.8), seed: 490 + i });
      });
      if (T > tm.who.pepper) hand('Pepper: innocent', 0, R.h / 2 - 40, 38, tm.who.pepper, { color: RED, seed: 495, dur: 0.4, rot: -0.03 });
      g.restore();
      note('reasons', ["Florida's reasons: dishonesty · missing information · self-deception"].concat(t > tm.who.marcus ? ['Marcus: dishonesty.'] : [], t > tm.who.bea ? ['Bea: missing information.'] : [], t > tm.who.pepper ? ['Pepper: innocent.'] : []));
    }
    const st = stage(t, part, lines_ ? [[0, 'two'], [tm.move, 'inset']] : [[0, 'inset']]);
    cast(t, ctx, st);
  };
  S.s4 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 2.4, after: 2.0 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: 'pop', t: c.W('s4_01', 'Bea'), gain: 0.3 }, { name: 'whoosh', t: c.L('s4_02') - 0.3, gain: 0.2 }, { name: 'scribble', t: c.L('s4_02'), gain: 0.25 }, { name: 'scribble', t: c.W('s4_02', 'He'), gain: 0.25 });
      out.push({ name: 'scribble', t: branch(c, { q4_right: 'truth', q4_wrong0: 'know', q4_wrong2: 'Bea' }), gain: 0.25 }, { name: 'scribble', t: branch(c, { q4_right: 'dragon', q4_wrong0: 'honest', q4_wrong2: 'honest' }), gain: 0.25 });
      const tg = branch(c, { q4_right: 'missing', q4_wrong0: 'missing', q4_wrong2: 'missing' });
      out.push({ name: 'pop', t: tg, gain: 0.3 }, { name: fx('string_pull', 'whoosh'), t: tg + 0.25, gain: 0.2 }, { name: 'paper', t: tg + 0.7, gain: 0.25 });
      out.push({ name: 'paper', t: c.W('s4_04', 'Florida'), gain: 0.3 });
      ['lying', 'missing', 'fooling'].forEach(w => out.push({ name: 'scribble', t: c.W('s4_04', w), gain: 0.2 }));
      out.push({ name: 'pop', t: c.W('s4_05', 'Marcus'), gain: 0.25 }, { name: 'pop', t: c.W('s4_05', 'Bea'), gain: 0.25 }, { name: 'ding', t: c.W('s4_05', 'Pepper'), gain: 0.25 });
      return out;
    },
  };

  /* ======================================================================
     s5 "Fact-check the narrator": the clue checklist (Narration.CLUES, the seven tags), Booth's card (N7), the Dick
     strip (N15) with a rolling linocut eyeball, NEW RULE, FACT-CHECKED
     ====================================================================== */
  const S5 = { list: { x: 440, y: 420, w: 700, h: 700 }, booth: { x: 1340, y: 160, w: 780, h: 180 }, dick: { x: 1350, y: 455, w: 980, h: 330 } };
  const ASKED = { 'says-vs-does': 'actions', 'others-react': 'react', 'something-to-gain': 'gain' };   // the clues s5_01 names
  const dickLay = () => { const D = S5.dick; return layout('dick', [DICK.reaction], D.x - D.w / 2 + 50, D.y + 60, D.w - 100, 30, 44, MEAS.type); };
  function eyeball(x, y, r, rot, seed) {
    g.save(); g.translate(x, y); g.rotate(rot);
    g.fillStyle = WHITE; g.beginPath(); g.arc(0, 0, r, 0, TAU); g.fill();
    g.strokeStyle = 'rgba(200,40,30,0.7)'; g.lineWidth = 2;
    for (let k = 0; k < 5; k++) { const a = k * 1.3 + 0.4; g.beginPath(); g.moveTo(Math.cos(a) * r * 0.95, Math.sin(a) * r * 0.95); g.quadraticCurveTo(Math.cos(a + 0.2) * r * 0.7, Math.sin(a + 0.2) * r * 0.7, Math.cos(a - 0.1) * r * 0.55, Math.sin(a - 0.1) * r * 0.55); g.stroke(); }
    g.fillStyle = '#52c8ff'; g.beginPath(); g.arc(r * 0.45, 0, r * 0.4, 0, TAU); g.fill();
    g.fillStyle = INK; g.beginPath(); g.arc(r * 0.5, 0, r * 0.2, 0, TAU); g.fill();
    g.fillStyle = WHITE; g.beginPath(); g.arc(r * 0.42, -r * 0.1, r * 0.07, 0, TAU); g.fill();
    g.restore();
    inkCircle(x, y, r, { w: 5, color: INK, seed });
  }
  BODY.s5 = function (t, part, ctx, c) {
    const Zn = Z(), Lc = S5.list, D = S5.dick, Bo = S5.booth;
    const ids = Narration.CLUE_IDS;
    const tm = {
      list: c.L('s5_01'), listEnd: c.W('s5_01', 'actions'), ask: {},
      booth: c.W('s5_02', 'Booth'), std: c.W('s5_02', 'standard'),
      dick: c.W('s5_03', 'Eyes'), quote: c.W('s5_03', 'his'), decides: c.W('s5_03', 'decides'), rollEnd: c.E('s5_04'),
      figure: c.W('s5_04', 'figure'), literally: c.W('s5_04', 'literally'),
      rule: c.W('s5_05', 'New'), stamp: c.W('s5_05', 'Fact') + 0.15,
    };
    for (const id in ASKED) tm.ask[id] = c.W('s5_01', ASKED[id]);
    Zn.cork();
    // the checklist
    const sl = pop(t, tm.list, 0.4, 1.6);
    if (sl > 0) {
      g.save(); g.translate(Lc.x, Lc.y); g.rotate(-0.012); g.scale(sl, sl);
      cardP(0, 0, Lc.w, Lc.h, { seed: 501, title: 'READ LIKE A DETECTIVE', t0: tm.list });
      const step = (tm.listEnd - tm.list) / ids.length;
      ids.forEach((id, i) => {
        const y = -Lc.h / 2 + 130 + i * 80, t0 = tm.list + 0.2 + i * Math.max(0.12, Math.min(0.4, step));
        if (T < t0) return;
        g.strokeStyle = INK; g.lineWidth = 3; g.strokeRect(-Lc.w / 2 + 34, y - 16, 32, 32);
        const w = labelW(id, 28);
        Zn.label(id, -Lc.w / 2 + 90 + w / 2, y, { size: 28, rot: ((i % 3) - 1) * 0.015, s: pop(T, t0, 0.3, 1.8), seed: 510 + i });
        const tk = tm.ask[id];
        if (tk != null && T > tk) ink([[-Lc.w / 2 + 38, y], [-Lc.w / 2 + 48, y + 12], [-Lc.w / 2 + 70, y - 22]], { w: 5, color: RED, seed: 520 + i, prog: P(T, tk, tk + 0.25), passes: 1 });
      });
      g.restore();
      prop('magnifier', Lc.x + Lc.w / 2 - 20, Lc.y + Lc.h / 2 + 10, 250, { rot: 0.35, s: pop(t, tm.listEnd, 0.35, 1.8), seed: 531 });
      note('list', ['Read like a detective. The clues:'].concat(ids.filter((id, i) => t > tm.list + 0.2 + i * 0.12).map(id => `${Narration.CLUES[id].name}: ${Narration.CLUES[id].ask}`)));
    }
    // Booth (s5_02)
    const sb = pop(t, tm.booth, 0.35, 1.8);
    if (sb > 0) {
      g.save(); g.translate(Bo.x, Bo.y); g.rotate(0.012); g.scale(sb, sb);
      Zn.page(0, 0, Bo.w, Bo.h, { seed: 541, tape: 1, dark: 0.35 });
      Zn.type(BOOTH.line, -Bo.w / 2 + 40, -30, 30, { t0: tm.booth, cps: 40, seed: 542 });
      hand(BOOTH.note, -Bo.w / 2 + 40, 34, 36, tm.std, { align: 'left', color: '#2b4a8c', seed: 543, dur: 0.6 });
      g.restore();
      note('booth', [`${BOOTH.line}: ${BOOTH.note}.`]);
    }
    // "The Eyes Have It" (s5_03)
    const sd = pop(t, tm.dick, 0.35, 1.6);
    if (sd > 0) {
      g.save(); g.translate(D.x, D.y); g.rotate(-0.01); g.scale(sd, sd); g.translate(-D.x, -D.y);
      Zn.page(D.x, D.y, D.w, D.h, { seed: 551, staple: 'top', dark: 0.45 });
      Zn.type(DICK.src, D.x + D.w / 2 - 40, D.y - D.h / 2 + 36, 22, { align: 'right', color: GREY, seed: 552 });
      const qx = D.x - D.w / 2 + 50, qy = D.y - 60;
      if (T > tm.figure) Zn.highlight(qx - 6, qy + 2, Zn.measure(DICK.quote, 38) + 12, 44, COL('cant-be-true'), { t0: tm.figure, dur: 0.4, seed: 553 });
      Zn.type(DICK.quote, qx, qy, 38, { t0: tm.quote, cps: cpsBetween(DICK.quote.length, tm.quote, tm.decides), seed: 554 });
      if (T > tm.decides) hand('the narrator decides:', qx, D.y + 12, 30, tm.decides, { align: 'left', color: GREY, seed: 555, dur: 0.3 });
      const lay = dickLay();
      if (T > tm.decides) typeSent(lay[0], tm.decides + 0.3, cpsBetween(DICK.reaction.length, tm.decides + 0.3, c.E('s5_03')), { seed: 556 });
      g.restore();
      note('dick', [`"${DICK.quote}"`, `The narrator decides: "${DICK.reaction}"`, `(${DICK.src})`]);
    }
    // the eyeball, off on its own across the board
    if (t > tm.decides && t < tm.rule + 0.6) alpha(1 - P(t, tm.rule, tm.rule + 0.5), () => {
      const u = E.inOutSine(P(t, tm.decides, tm.rollEnd)), r = 40, x = lerp(D.x - D.w / 2 + 40, D.x - 60, u), y = D.y + D.h / 2 + 50 - Math.abs(Math.sin(u * Math.PI * 3)) * 26;
      eyeball(x, y, r, (x - D.x) / r, 561);
    });
    if (t > tm.literally) {
      sticky(['a figure of speech,', 'read literally'], D.x + D.w / 2 - 170, D.y + D.h / 2 + 60, tm.literally, { w: 330, size: 32, seed: 571, rot: 0.05 });
      note('literal', ['He read a figure of speech literally.']);
    }
    // NEW RULE (s5_05) + FACT-CHECKED
    if (t > tm.rule) {
      sticky(['NEW RULE:', "in writing doesn't", "mean it's true"], D.x - 110, D.y + D.h / 2 + 90, tm.rule, { w: 330, size: 34, seed: 581, rot: 0.06 });
      note('rule', ["New rule: it's in writing doesn't mean it's true."]);
    }
    stampMark('FACT-CHECKED', 1160, 872, stampS(t, tm.stamp), { rot: -0.05, size: 84 });   // on the open cork under the stickies: the Dick quote stays readable
    MARKS.s5 = { stamp: t > tm.stamp + 0.3 };
    if (t > tm.stamp) note('stamp', ['FACT-CHECKED']);
    const st = stage(t, part, [[0, 'two'], [c.L('s5_01') - 0.3, 'inset']]);
    cast(t, ctx, st);
  };
  S.s5 = {
    timing: { lead: { lines: CARD.lead }, hold: { lines: 3.0 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: 'paper', t: c.L('s5_01'), gain: 0.3 });
      Object.values(ASKED).forEach(w => out.push({ name: 'scribble', t: c.W('s5_01', w), gain: 0.2 }));
      out.push({ name: 'pop', t: c.W('s5_02', 'Booth'), gain: 0.3 }, { name: 'paper', t: c.W('s5_03', 'Eyes'), gain: 0.3 }, { name: fx('typewriter', 'scribble'), t: c.W('s5_03', 'his'), gain: 0.25 });
      out.push({ name: 'boing', t: c.W('s5_03', 'decides'), gain: 0.25 }, { name: 'pop', t: c.W('s5_04', 'literally'), gain: 0.25 }, { name: 'pop', t: c.W('s5_05', 'New'), gain: 0.3 });
      out.push({ name: fx('stamp_thump', 'stamp'), t: c.W('s5_05', 'Fact') + 0.15, gain: 0.7 }, { name: 'sparkle', t: c.W('s5_05', 'Fact') + 0.4, gain: 0.3 });
      return out;
    },
  };

  /* wrap every scene: the chapter card that tears away to the scene; sfx outside the part are dropped */
  for (const id of Object.keys(BODY)) {
    S[id].draw = function (t, part, ctx) {
      const c = cues(ctx), i = segIndex(part.seg);
      for (const k in NOTES) delete NOTES[k];
      if (part.kind === 'lines' && t < CARD.hold + CARD.tear) {
        if (t < CARD.hold) drawCard(i);
        else tornTransition(() => drawCard(i), u => BODY[id](u, part, ctx, c), t, CARD.hold, CARD.tear, 40 + i);
      } else BODY[id](t, part, ctx, c);
    };
    const sfx = S[id].sfx;
    S[id].sfx = (part, ctx) => sfx(part, ctx).filter(e => e && e.name && e.t > -1e5 && e.t < 1e5);
  }

  /* the placeholder for any segment the script adds later (pure in t): its chapter title + the storyboard note */
  const STAGE = {
    draw(t, part, ctx) {
      Z().cork();
      ransom(String(part.seg.chapter || part.seg.id).toUpperCase(), 960, 110, 64, -1e6, { seed: 31, palette: COVER_PAL });
      const lines = UI._wrap(part.seg.visual || '', 760, `34px ${HANDF}`).slice(0, 6), h = 60 + lines.length * 44;
      Z().page(960, 400, 860, h, { seed: 12, tape: 1 });
      lines.forEach((l, i) => hand(l, 560, 400 - h / 2 + 52 + i * 44, 34, -1e6, { dur: 0.01, align: 'left', color: INK, seed: 20 + i }));
      Puppet.draw('nia', 300, 1060, 600, { env: ctx.speaking('nia'), facing: 1, t });
      Puppet.draw('park', 1620, 1060, 660, { env: ctx.speaking('park'), facing: -1, t });
    },
  };

  window.NarratorVideo = { BODY, layers: LY, notes: NOTES, marks: MARKS };

  class VideoScene extends Player.Scene {
    enter() {
      for (const sg of (Game.content.script || { segments: [] }).segments) if (!S[sg.id]) S[sg.id] = STAGE;
      super.enter();
      // bake the prop stand-ins in spare frame time while the first chapter card is up
      if (typeof prewarm === 'function') for (const n of Object.keys(PROPS)) if (!IMG[n] || n === 'bea') prewarm.draw(() => { g.save(); g.translate(-5000, -5000); prop(n, 0, 0, PROPS[n].h); g.restore(); });
    }
  }
  Levels.video = VideoScene;
})();
